import { useCallback, useEffect, useMemo, useState } from "react"

import { SearchOutlined } from "@ant-design/icons"
import { Input } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import MultipleSelect from "../../../Components/MultipleSelect/MultipleSelect"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import { AdminReceiptTitle } from "../components/AdminReceiptTitle/AdminReceiptTitle"
import ConfigureTabs, {
  RECEIPT_CONFIGURE_TABS
} from "../ConfigureProfilePage/components/ConfigureTabs/ConfigureTabs"
import { loadProfile } from "../ConfigureProfilePage/constants/externalOps"
import { hydrateConfigure, selectWizard } from "../ConfigureProfilePage/redux/configure.slice"
import {
  buildingBlocksFromConfigure,
  overlayStatusFromView,
  wizardView
} from "../ConfigureProfilePage/utils/hydrate"
import { useReceiptBusinessId } from "../hooks/useReceiptBusinessId"
import { ActivationModal } from "./components/ActivationModal/ActivationModal"
import CodesPanel from "./components/CodesPanel/CodesPanel"
import { DescriptorList } from "./components/DescriptorList/DescriptorList"
import { ProgramCard } from "./components/ProgramCard/ProgramCard"
import ProgramCardSkeleton from "./components/ProgramCard/ProgramCardSkeleton"
import { ProgramTabs } from "./components/ProgramTabs/ProgramTabs"
import StatusLogsModal from "./components/StatusLogsModal/StatusLogsModal"
import { CODES_VIEW, CODES_VIEW_LINK_LABEL, STATUS_META } from "./constants/constants"
import {
  selectCodes,
  selectDescriptors,
  selectPrograms,
  selectProgramsError
} from "./redux/business.slice"
import {
  getCodes,
  getDescriptors,
  getPrograms,
  setDescriptorStatus,
  setMatchType
} from "./redux/business.thunks"

import shellStyles from "../ConfigureProfilePage/shell.module.less"
import styles from "./shell.module.less"

const ConfigureBusinessPage = ({ usersViewForAdmin }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const programs = useSelector(selectPrograms)
  const programsError = useSelector(selectProgramsError)
  // BA-scoped fetches must refetch on account switch, so every request below
  // takes this id rather than letting the server fall back to the session BA.
  const businessId = useReceiptBusinessId(usersViewForAdmin)

  // The Profile tab's lock (ConfigureTabs) needs to know its overlayStatus even
  // when the merchant lands here first - Configure state isn't hydrated by this
  // page's own thunks, so it's fetched the same way ConfigureProfilePage does.
  const isDemoAccount = useSelector((s) => !!s.auth?.userInfo?.is_demo)
  const isExternalDemo = useExternalDemoMode()
  const isDemoMode = isDemoAccount || isExternalDemo
  const wizard = useSelector(selectWizard)
  const overlayStatus = overlayStatusFromView(wizardView(wizard))

  useEffect(() => {
    if (isDemoMode) return undefined
    let cancelled = false

    loadProfile(businessId).then((result) => {
      if (cancelled || !result.ok) return
      dispatch(
        hydrateConfigure({
          baId: businessId || "none",
          blocks: buildingBlocksFromConfigure(result.state),
          wizard: result.state.wizard,
          baseVersionId: result.state.baseVersionId
        })
      )
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, isDemoMode, businessId])

  const [activeProgram, setActiveProgram] = useState("cc")
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState(Object.keys(STATUS_META))
  const [activationOpen, setActivationOpen] = useState(false)
  const [logsRow, setLogsRow] = useState(null)
  // Which view the right pane shows (BLOCK 5). Component state, never persisted:
  // the page must always open on the descriptor list.
  const [codesView, setCodesView] = useState(CODES_VIEW.DESCRIPTORS)

  const descriptors = useSelector(selectDescriptors(activeProgram))
  const codes = useSelector(selectCodes)
  const availablePrograms = programs || []

  // The toggle belongs to Order Insight and to support only (BLOCK 5.2).
  const codesToggleEnabled = usersViewForAdmin && activeProgram === "oi"
  const isCodesView = codesView !== CODES_VIEW.DESCRIPTORS

  // Descriptors first, then whichever code views this BA actually has — the
  // server sends `available_code_types` already in cycle order, so a BA with one
  // code type yields a 2-state cycle and one with none yields no toggle at all.
  const codesCycle = useMemo(
    () => [CODES_VIEW.DESCRIPTORS, ...(codes.availableCodeTypes || [])],
    [codes.availableCodeTypes]
  )

  const nextCodesView = codesCycle[(codesCycle.indexOf(codesView) + 1) % codesCycle.length]

  // Leaving Order Insight (or switching account) drops back to the descriptor
  // list, so the toggle never carries a code view onto a card that has none.
  useEffect(() => {
    setCodesView(CODES_VIEW.DESCRIPTORS)
  }, [activeProgram, businessId])

  useEffect(() => {
    dispatch(getPrograms(businessId))
  }, [dispatch, businessId])

  // Debounced refetch on filter changes — always resets to page 1; subsequent
  // pages are appended via handleLoadMore (infinite scroll, BLOCK 3.3).
  useEffect(() => {
    const id = setTimeout(() => {
      dispatch(
        getDescriptors({
          program: activeProgram,
          page: 1,
          search,
          status: statusFilter,
          businessAccountId: businessId
        })
      )
    }, 250)
    return () => clearTimeout(id)
  }, [dispatch, activeProgram, search, statusFilter, businessId])

  // Codes are fetched even while the descriptor list is showing: the response's
  // `available_code_types` is what tells the link which view is next, and whether
  // there is anything to cycle to at all. In that state the request is only for
  // that list, so `code_type` is an arbitrary valid value and `search` is left
  // out — there the search box still filters descriptors (BLOCK 5.8).
  useEffect(() => {
    if (!codesToggleEnabled) return undefined
    const id = setTimeout(() => {
      dispatch(
        getCodes({
          codeType: isCodesView ? codesView : CODES_VIEW.ARN,
          search: isCodesView ? search : "",
          businessAccountId: businessId
        })
      )
    }, 250)
    return () => clearTimeout(id)
  }, [dispatch, codesToggleEnabled, isCodesView, codesView, search, businessId])

  const handleLoadMore = useCallback(() => {
    if (descriptors?.fetching) return
    if (descriptors?.rows?.length >= descriptors?.count) return
    dispatch(
      getDescriptors({
        program: activeProgram,
        page: (descriptors?.page || 1) + 1,
        search,
        status: statusFilter,
        businessAccountId: businessId
      })
    )
  }, [
    dispatch,
    activeProgram,
    search,
    statusFilter,
    businessId,
    descriptors?.fetching,
    descriptors?.rows?.length,
    descriptors?.count,
    descriptors?.page
  ])

  // Support's two row controls (BLOCK 3.4/3.5). No confirmation and no optimistic
  // update: the reducers apply whatever the server settled on, and re-picking the
  // current value never reaches here — the dropdowns treat that as a no-op.
  //
  // The status write also refetches the list it was made from, so match type
  // follows the new status without a page reload (CHA1-2614) — hence the current
  // filters travel with it. Back to page 1: the pages loaded by infinite scroll
  // are not addressable in one request (fixed page size).
  const handleStatusChange = useCallback(
    (row, status) =>
      dispatch(
        setDescriptorStatus({
          recordId: row.record_id,
          status,
          list: {
            program: activeProgram,
            page: 1,
            search,
            status: statusFilter,
            businessAccountId: businessId
          }
        })
      ),
    [dispatch, activeProgram, search, statusFilter, businessId]
  )

  const handleMatchTypeChange = useCallback(
    (row, matchType) =>
      dispatch(setMatchType({ recordId: row.record_id, matchType, businessAccountId: businessId })),
    [dispatch, businessId]
  )

  const program = useMemo(
    () => (programs || []).find((p) => p.program === activeProgram) || null,
    [programs, activeProgram]
  )

  const statusOptions = useMemo(
    () => Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: t(meta.label) })),
    [t]
  )

  // Filters gate the "no descriptors yet" check: an empty result under an
  // active filter is not the same as an empty program.
  const filtersActive = search !== "" || statusFilter.length !== Object.keys(STATUS_META).length

  return (
    <div className={shellStyles.shell}>
      <AdminReceiptTitle usersViewForAdmin={usersViewForAdmin} />

      <ConfigureTabs
        activeTab={RECEIPT_CONFIGURE_TABS.BUSINESS}
        overlayStatus={overlayStatus}
        usersViewForAdmin={usersViewForAdmin}
      />

      <div className={styles.wrap} data-testid="deflect-business-page">
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            <ProgramTabs
              programs={availablePrograms}
              active={activeProgram}
              onChange={setActiveProgram}
            />

            {/*{programsFetching && !program ? (*/}
            {!program ? (
              <ProgramCardSkeleton />
            ) : programsError ? (
              <div className={styles.card}>{t("Failed to load program")}</div>
            ) : program ? (
              <ProgramCard
                program={program}
                onAdd={() => setActivationOpen(true)}
                disabled={
                  descriptors?.fetching ||
                  (program?.status !== "active" &&
                    !filtersActive &&
                    descriptors?.rows?.length === 0)
                }
                disabledReason="Submit your Digital Receipt profile first to add descriptors."
                codesToggleLabel={
                  codesToggleEnabled && codesCycle.length > 1
                    ? CODES_VIEW_LINK_LABEL[nextCodesView]
                    : null
                }
                onCodesToggle={() => setCodesView(nextCodesView)}
              />
            ) : null}
          </div>

          <div className={styles.listPane}>
            <div className={styles.filters}>
              <Input
                allowClear
                size="middle"
                prefix={<SearchOutlined />}
                placeholder={isCodesView ? t("Search codes...") : t("Search descriptors...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.searchInput}
                data-testid="deflect-business-search-input"
              />
              {/* Statuses do not apply to codes, so the filter is hidden while a
                  codes view is open and returns with the list (BLOCK 5.8). */}
              {isCodesView ? null : (
                <MultipleSelect
                  options={statusOptions}
                  values={statusFilter}
                  onChange={setStatusFilter}
                  allBtnText={t("All statuses")}
                  mixedValuesText={t("Statuses: Mixed Values")}
                  oneSelectedText={t("Status: ")}
                  className={styles.statusSelect}
                />
              )}
            </div>

            {isCodesView ? (
              <CodesPanel view={codesView} codes={codes} />
            ) : (
              <DescriptorList
                descriptors={descriptors}
                onRowLogsClick={setLogsRow}
                hasMore={descriptors?.rows?.length < descriptors?.count}
                onLoadMore={handleLoadMore}
                usersViewForAdmin={usersViewForAdmin}
                onStatusChange={handleStatusChange}
                onMatchTypeChange={handleMatchTypeChange}
              />
            )}
          </div>
        </div>
      </div>

      {activationOpen && (
        <ActivationModal
          program={program}
          onClose={() => setActivationOpen(false)}
          businessAccountId={businessId}
        />
      )}

      <StatusLogsModal
        row={logsRow}
        onClose={() => setLogsRow(null)}
        businessAccountId={businessId}
      />
    </div>
  )
}

export default ConfigureBusinessPage
